import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { inject } from '@angular/core';
import { ErrorStore } from '@application/errors/data-access';
import { HttpErrorResponse } from '@angular/common/http';
import { setPropError, setPropInit, setPropLoaded, setPropLoading, withReqState } from '@application/data-access';
import { pipe, switchMap, tap } from 'rxjs';
import { HotsiteService } from '../infra/hotsite.service';
import { THotsite, THotsiteConfiguration } from '../entities/hotsite.model';
import { APP_CONFIG } from '@application/config';
import { DxOdataStoreService } from '@shared/components/dx-data-grid/data-access';
import { tapResponse } from '@ngrx/operators';


export interface IHotsiteStore {
    configuration: THotsiteConfiguration;
    link: string;
    offerPage: THotsite;
    contactPage: THotsite;
    registerPage: THotsite;
    showConfig: boolean;
    showOfferForm: boolean;
    showContactForm: boolean;
    showRegisterForm: boolean;
}

export const hotsiteInitialStore: IHotsiteStore = {
    configuration: null,
    link: null,
    offerPage: null,
    contactPage: null,
    registerPage: null,
    showConfig: false,
    showOfferForm: false,
    showContactForm: false,
    showRegisterForm: false,
};

export const HotsiteStore = signalStore(
  { providedIn: 'root' },
  withState<IHotsiteStore>(hotsiteInitialStore),
  withMethods((store, errorStore = inject(ErrorStore), hotsiteService = inject(HotsiteService), appConfig = inject(APP_CONFIG), dxOdataStoreService = inject(DxOdataStoreService)) => {

    const hotsiteLink = (subDomainParam: string, path?: string) => {
        const subDomain = subDomainParam ?? '*';
        const baseUrl = appConfig.environment === 'prod' ? `https://${subDomain}.novo.actuar.com` : `https://${subDomain}.stage.novo.actuar.com`;
        return path ? `${baseUrl}/${path}` : baseUrl;
    }

    return {
      hotsiteLink,

      toggleConfigModal: (showConfig = !store.showConfig()) => {
        patchState(store, { showConfig });
      },

      toggleOfferFormModal: (showOfferForm = !store.showOfferForm()) => {
        patchState(store, { showOfferForm });
      },

      toggleContactFormModal: (showContactForm = !store.showContactForm()) => {
        patchState(store, { showContactForm });
      },

      toggleRegisterFormModal: (showRegisterForm = !store.showRegisterForm()) => {
        patchState(store, { showRegisterForm });
      },

      getContactPagesDataSource: () => {
          return dxOdataStoreService.fromOdata({
              url: appConfig.apiUrl.odata_hotsite_service, metadata: 'HotSites', key: 'HotsiteId', params: {
                  $select: 'Name,Description,HotsiteId,HotsiteType,Path',
                  $filter: '(HotsiteType eq 0)'
              }
          })
      },
      getOfferPagesDataSource: () => {
          return dxOdataStoreService.fromOdata({
              url: appConfig.apiUrl.odata_hotsite_service, metadata: 'HotSites', key: 'HotsiteId', params: {
                  $select: 'Name,Description,HotsiteId,HotsiteType,Path',
                  $filter: '(HotsiteType eq 1)'
              }
          })
      },
      getRegisterPagesDataSource: () => {
          return dxOdataStoreService.fromOdata({
              url: appConfig.apiUrl.odata_hotsite_service, metadata: 'HotSites', key: 'HotsiteId', params: {
                  $select: 'Name,Description,HotsiteId,HotsiteType,Path',
                  $filter: '(HotsiteType eq 2)'
              }
          })
      },
      getHotsiteConfiguration: rxMethod<string>(pipe(
          tap(() => patchState(store, { ...setPropLoading('getHotsiteConfiguration') })),
          switchMap((payload) => hotsiteService.getHotsiteConfiguration(payload).pipe(
            tapResponse({
              next: (configuration) => {
                  const link = hotsiteLink(configuration?.Url);
                  patchState(store, { configuration, link, ...setPropLoaded('getHotsiteConfiguration')})
              },
              error: (error: HttpErrorResponse) => {
                console.error(error);
                patchState( store, { ...setPropError('getHotsiteConfiguration', error) });
                errorStore.setHttpError(error)
              }
            }),
          ))
      )),

      updateConfiguration: rxMethod<THotsiteConfiguration>(pipe(
          tap(() => patchState(store, { ...setPropLoading('updateConfiguration') })),
          switchMap((payload) => hotsiteService.updateHotsiteConfiguration(payload).pipe(
            tapResponse({
              next: (configuration) => {
                  const link = hotsiteLink(configuration?.Url);
                  patchState(store, { configuration, link, ...setPropLoaded('updateConfiguration')})
              },
              error: (error: HttpErrorResponse) => {
                console.error(error);
                patchState( store, { ...setPropError('updateConfiguration', error) });
                errorStore.setHttpError(error)
              }
            }),
          ))
      )),

      getOfferPage: rxMethod<string>(pipe(
        tap(() => patchState(store, { ...setPropLoading('getOfferPage') })),
        switchMap((payload) => hotsiteService.getById(payload).pipe(
          tapResponse({
            next: (offerPage) => {
                patchState(store, { offerPage, ...setPropLoaded('getOfferPage')})
            },
            error: (error: HttpErrorResponse) => {
              console.error(error);
              patchState( store, { ...setPropError('getOfferPage', error) });
              errorStore.setHttpError(error)
            }
          }),
        ))
      )),

      getContactPage: rxMethod<string>(pipe(
        tap(() => patchState(store, { ...setPropLoading('getContactPage') })),
        switchMap((payload) => hotsiteService.getById(payload).pipe(
          tapResponse({
            next: (contactPage) => {
                patchState(store, { contactPage, ...setPropLoaded('getContactPage')})
            },
            error: (error: HttpErrorResponse) => {
              console.error(error);
              patchState( store, { ...setPropError('getContactPage', error) });
              errorStore.setHttpError(error)
            }
          }),
        ))
      )),

      getRegisterPage: rxMethod<string>(pipe(
        tap(() => patchState(store, { ...setPropLoading('getRegisterPage') })),
        switchMap((payload) => hotsiteService.getById(payload).pipe(
          tapResponse({
            next: (registerPage) => {
                patchState(store, { registerPage, ...setPropLoaded('getRegisterPage')})
            },
            error: (error: HttpErrorResponse) => {
              console.error(error);
              patchState( store, { ...setPropError('getRegisterPage', error) });
              errorStore.setHttpError(error)
            }
          }),
        ))
      )),

      deleteOfferPage: rxMethod<string>(pipe(
        tap(() => patchState(store, { ...setPropLoading('deleteOfferPage') })),
        switchMap((payload) => hotsiteService.deleteHotsite(payload).pipe(
          tapResponse({
            next: () => {
                patchState(store, { ...setPropLoaded('deleteOfferPage')})
            },
            error: (error: HttpErrorResponse) => {
              console.error(error);
              patchState( store, { ...setPropError('deleteOfferPage', error) });
              errorStore.setHttpError(error)
            }
          }),
        ))
      )),

      deleteContactPage: rxMethod<string>(pipe(
        tap(() => patchState(store, { ...setPropLoading('deleteContactPage') })),
        switchMap((payload) => hotsiteService.deleteHotsite(payload).pipe(
          tapResponse({
            next: () => {
                patchState(store, { ...setPropLoaded('deleteContactPage')})
            },
            error: (error: HttpErrorResponse) => {
              console.error(error);
              patchState( store, { ...setPropError('deleteContactPage', error) });
              errorStore.setHttpError(error)
            }
          }),
        ))
      )),

      deleteRegisterPage: rxMethod<string>(pipe(
        tap(() => patchState(store, { ...setPropLoading('deleteRegisterPage') })),
        switchMap((payload) => hotsiteService.deleteHotsite(payload).pipe(
          tapResponse({
            next: () => {
                patchState(store, { ...setPropLoaded('deleteRegisterPage')})
            },
            error: (error: HttpErrorResponse) => {
              console.error(error);
              patchState( store, { ...setPropError('deleteRegisterPage', error) });
              errorStore.setHttpError(error)
            }
          }),
        ))
      )),

      updateContactPage: rxMethod<THotsite>(pipe(
        tap(() => patchState(store, { ...setPropLoading('updateContactPage') })),
        switchMap((payload) => hotsiteService.updateHotsite(payload).pipe(
          tapResponse({
            next: (contactPage) => {
                patchState(store, { contactPage, ...setPropLoaded('updateContactPage')})
            },
            error: (error: HttpErrorResponse) => {
              console.error(error);
              patchState( store, { ...setPropError('updateContactPage', error) });
              errorStore.setHttpError(error)
            }
          }),
        ))
      )),

      createContactPage: rxMethod<THotsite>(pipe(
        tap(() => patchState(store, { ...setPropLoading('createContactPage') })),
        switchMap((payload) => hotsiteService.createHotsite(payload).pipe(
          tapResponse({
            next: () => {
                patchState(store, { ...setPropLoaded('createContactPage')})
            },
            error: (error: HttpErrorResponse) => {
              console.error(error);
              patchState( store, { ...setPropError('createContactPage', error) });
              errorStore.setHttpError(error)
            }
          }),
        ))
      )),

      updateRegisterPage: rxMethod<THotsite>(pipe(
        tap(() => patchState(store, { ...setPropLoading('updateRegisterPage') })),
        switchMap((payload) => hotsiteService.updateHotsite(payload).pipe(
          tapResponse({
            next: (registerPage) => {
                patchState(store, { registerPage, ...setPropLoaded('updateRegisterPage')})
            },
            error: (error: HttpErrorResponse) => {
              console.error(error);
              patchState( store, { ...setPropError('updateRegisterPage', error) });
              errorStore.setHttpError(error)
            }
          }),
        ))
      )),

      createRegisterPage: rxMethod<THotsite>(pipe(
        tap(() => patchState(store, { ...setPropLoading('createRegisterPage') })),
        switchMap((payload) => hotsiteService.createHotsite(payload).pipe(
          tapResponse({
            next: () => {
                patchState(store, { ...setPropLoaded('createRegisterPage')})
            },
            error: (error: HttpErrorResponse) => {
              console.error(error);
              patchState( store, { ...setPropError('createRegisterPage', error) });
              errorStore.setHttpError(error)
            }
          }),
        ))
      )),

      updateOfferPage: rxMethod<THotsite>(pipe(
        tap(() => patchState(store, { ...setPropLoading('updateOfferPage') })),
        switchMap((payload) => hotsiteService.updateHotsite(payload).pipe(
          tapResponse({
            next: (offerPage) => {
                patchState(store, { offerPage, ...setPropLoaded('updateOfferPage')})
            },
            error: (error: HttpErrorResponse) => {
              console.error(error);
              patchState( store, { ...setPropError('updateOfferPage', error) });
              errorStore.setHttpError(error)
            }
          }),
        ))
      )),

      createOfferPage: rxMethod<THotsite>(pipe(
        tap(() => patchState(store, { ...setPropLoading('createOfferPage') })),
        switchMap((payload) => hotsiteService.createHotsite(payload).pipe(
          tapResponse({
            next: () => {
                patchState(store, { ...setPropLoaded('createOfferPage')})
            },
            error: (error: HttpErrorResponse) => {
              console.error(error);
              patchState( store, { ...setPropError('createOfferPage', error) });
              errorStore.setHttpError(error)
            }
          }),
        ))
      )),


      setPropsInit(props: string[]): void {
        patchState(store, {...setPropInit(...props)});
      },

      blankPagesState(): void {
        patchState(store, { offerPage: null, contactPage: null, registerPage: null }, setPropInit('getOfferPage', 'getContactPage', 'getRegisterPage', 'deleteOfferPage', 'deleteContactPage', 'deleteRegisterPage', 'updateContactPage', 'createContactPage', 'updateRegisterPage', 'createRegisterPage', 'createOfferPage', 'updateOfferPage'));
      }
    }
  }),
  withReqState('getHotsiteConfiguration', 'updateConfiguration', 'getOfferPage', 'getContactPage', 'getRegisterPage', 'deleteOfferPage', 'deleteContactPage', 'deleteRegisterPage', 'updateContactPage', 'createContactPage', 'updateRegisterPage', 'createRegisterPage', 'createOfferPage', 'updateOfferPage')
)


